import React, { useState, useEffect, useCallback } from 'react';
import { otaAPI, propertiesAPI, icalSyncAPI } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import {
  Loader2, Link2, Unlink2, RefreshCw, Settings, CheckCircle, XCircle, Clock,
  ExternalLink, Building2, ChevronRight, AlertTriangle, Zap, Globe, Activity,
  TrendingUp, TrendingDown, Calendar, BarChart3, AlertCircle, Copy, Check, Code
} from 'lucide-react';
import WidgetsSection from '@/pages/WordPressWidgetPage';

const API = process.env.REACT_APP_BACKEND_URL;

const IntegrationsPage = () => {
  const [channels, setChannels] = useState([]);
  const [properties, setProperties] = useState([]);
  const [icalSubscriptions, setIcalSubscriptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedChannel, setSelectedChannel] = useState(null);
  const [connectDialog, setConnectDialog] = useState(false);
  const [mappingDialog, setMappingDialog] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [credentials, setCredentials] = useState({ api_key: '', api_secret: '', partner_id: '', hotel_id: '' });
  const [selectedProperty, setSelectedProperty] = useState('');
  const [otaPropertyId, setOtaPropertyId] = useState('');
  const [activeTab, setActiveTab] = useState('health');
  const [copiedFeed, setCopiedFeed] = useState(null);

  // Health metrics
  const [healthMetrics, setHealthMetrics] = useState({
    totalSyncs: 0,
    successfulSyncs: 0,
    failedSyncs: 0,
    lastSyncTime: null,
    channelHealth: {}
  });

  const loadData = useCallback(async () => {
    try {
      const [channelsRes, propsRes, subsRes] = await Promise.all([
        otaAPI.listChannels(),
        propertiesAPI.list(),
        icalSyncAPI.listSubscriptions(),
      ]);
      setChannels(channelsRes.data);
      setProperties(propsRes.data);
      setIcalSubscriptions(subsRes.data || []);
      
      // Calculate health metrics
      const connected = channelsRes.data.filter(c => c.connected);
      const channelHealth = {};
      connected.forEach(c => {
        channelHealth[c.code] = {
          status: c.connection_status || 'unknown',
          lastSync: c.last_sync,
          errorCount: c.sync_errors?.length || 0,
          propertyCount: c.property_count || 0
        };
      });
      
      setHealthMetrics({
        totalSyncs: connected.length * 10, // Placeholder
        successfulSyncs: connected.filter(c => c.connection_status === 'connected').length * 10,
        failedSyncs: connected.filter(c => c.connection_status === 'error').length * 5,
        lastSyncTime: connected.reduce((latest, c) => {
          if (!c.last_sync) return latest;
          return !latest || new Date(c.last_sync) > new Date(latest) ? c.last_sync : latest;
        }, null),
        channelHealth
      });
    } catch (err) {
      console.error('Load error:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleConnect = async () => {
    if (!selectedChannel) return;
    setProcessing(true);
    try {
      await otaAPI.connectChannel(selectedChannel.code, {
        channel: selectedChannel.code,
        api_key: credentials.api_key,
        api_secret: credentials.api_secret,
        partner_id: credentials.partner_id,
        hotel_id: credentials.hotel_id,
      });
      setConnectDialog(false);
      setCredentials({ api_key: '', api_secret: '', partner_id: '', hotel_id: '' });
      loadData();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to connect. Please check credentials.');
    } finally {
      setProcessing(false);
    }
  };

  const handleDisconnect = async (channel) => {
    if (!window.confirm(`Are you sure you want to disconnect from ${channel.name}?`)) return;
    try {
      await otaAPI.disconnectChannel(channel.code);
      loadData();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to disconnect');
    }
  };

  const handleSync = async (channel) => {
    try {
      await otaAPI.syncChannel(channel.code, 'full');
      alert(`Sync started for ${channel.name}`);
      loadData();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to start sync');
    }
  };

  const handleMapProperty = async () => {
    if (!selectedChannel || !selectedProperty || !otaPropertyId) return;
    setProcessing(true);
    try {
      await otaAPI.mapProperty(selectedChannel.code, {
        local_property_id: selectedProperty,
        ota_property_id: otaPropertyId,
      });
      setMappingDialog(false);
      setSelectedProperty('');
      setOtaPropertyId('');
      loadData();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to map property');
    } finally {
      setProcessing(false);
    }
  };

  const copyToClipboard = async (text, feedId) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedFeed(feedId);
      setTimeout(() => setCopiedFeed(null), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const getStatusIcon = (channel) => {
    if (!channel.connected) return <XCircle className="w-4 h-4 text-gray-400" />;
    if (channel.connection_status === 'connected') return <CheckCircle className="w-4 h-4 text-emerald-500" />;
    if (channel.connection_status === 'error') return <AlertTriangle className="w-4 h-4 text-red-500" />;
    return <Clock className="w-4 h-4 text-amber-500" />;
  };

  const getChannelLogo = (code) => {
    const logos = {
      airbnb: '/images/airbnb-logo.png',
      booking_com: '/images/booking-logo.png',
      expedia: '/images/expedia-logo.png',
      agoda: '/images/agoda-logo.png',
      vrbo: '/images/vrbo-logo.png',
    };
    return logos[code];
  };

  const getChannelIcon = (code) => {
    const icons = {
      airbnb: '🏠',
      booking_com: '🅱️',
      expedia: '✈️',
      agoda: '🌴',
      vrbo: '🏡',
    };
    return icons[code] || '🔗';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-10 h-10 text-airbnb-pink animate-spin" />
      </div>
    );
  }

  const connectedChannels = channels.filter(c => c.connected);
  const availableChannels = channels.filter(c => !c.connected && c.api_status !== 'coming_soon');
  const comingSoonChannels = channels.filter(c => c.api_status === 'coming_soon');

  return (
    <div data-testid="integrations-page" className="animate-fade-in max-w-6xl mx-auto">
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-airbnb-gray">Integrations & Channel Manager</h1>
        <p className="text-airbnb-medium-gray mt-1">Connect your properties to OTA platforms and manage calendar sync</p>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full grid-cols-4 mb-6">
          <TabsTrigger value="health" className="data-[state=active]:bg-airbnb-pink data-[state=active]:text-white">
            <Activity className="w-4 h-4 mr-2" />
            Channel Health
          </TabsTrigger>
          <TabsTrigger value="channels" className="data-[state=active]:bg-airbnb-pink data-[state=active]:text-white">
            <Globe className="w-4 h-4 mr-2" />
            OTA Channels
          </TabsTrigger>
          <TabsTrigger value="ical" className="data-[state=active]:bg-airbnb-pink data-[state=active]:text-white">
            <Calendar className="w-4 h-4 mr-2" />
            iCal Feeds
          </TabsTrigger>
          <TabsTrigger value="widgets" className="data-[state=active]:bg-airbnb-pink data-[state=active]:text-white">
            <Code className="w-4 h-4 mr-2" />
            Widgets
          </TabsTrigger>
        </TabsList>

        {/* Channel Health Dashboard Tab */}
        <TabsContent value="health" className="space-y-6">
          <ChannelHealthDashboard 
            channels={channels}
            healthMetrics={healthMetrics}
            icalSubscriptions={icalSubscriptions}
            onSync={handleSync}
          />
        </TabsContent>

        {/* OTA Channels Tab */}
        <TabsContent value="channels" className="space-y-6">
          {/* Connected Channels */}
          {connectedChannels.length > 0 && (
            <div>
              <h2 className="text-lg font-semibold text-airbnb-gray mb-4 flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-emerald-500" />
                Connected Channels ({connectedChannels.length})
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {connectedChannels.map((channel) => (
                  <ChannelCard 
                    key={channel.code} 
                    channel={channel} 
                    onSync={handleSync}
                    onDisconnect={handleDisconnect}
                    onMapProperty={() => { setSelectedChannel(channel); setMappingDialog(true); }}
                    getStatusIcon={getStatusIcon}
                    getChannelIcon={getChannelIcon}
                  />
                ))}
              </div>
            </div>
          )}

          {/* Available to Connect */}
          {availableChannels.length > 0 && (
            <div>
              <h2 className="text-lg font-semibold text-airbnb-gray mb-4 flex items-center gap-2">
                <Link2 className="w-5 h-5 text-blue-500" />
                Available to Connect ({availableChannels.length})
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {availableChannels.map((channel) => (
                  <ChannelCard 
                    key={channel.code} 
                    channel={channel} 
                    onConnect={() => { setSelectedChannel(channel); setConnectDialog(true); }}
                    getStatusIcon={getStatusIcon}
                    getChannelIcon={getChannelIcon}
                  />
                ))}
              </div>
            </div>
          )}

          {/* Coming Soon */}
          {comingSoonChannels.length > 0 && (
            <div>
              <h2 className="text-lg font-semibold text-airbnb-gray mb-4 flex items-center gap-2">
                <Clock className="w-5 h-5 text-amber-500" />
                Coming Soon ({comingSoonChannels.length})
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {comingSoonChannels.map((channel) => (
                  <ChannelCard 
                    key={channel.code} 
                    channel={channel} 
                    getStatusIcon={getStatusIcon}
                    getChannelIcon={getChannelIcon}
                    comingSoon
                  />
                ))}
              </div>
            </div>
          )}
        </TabsContent>

        {/* iCal Feeds Tab */}
        <TabsContent value="ical" className="space-y-6">
          <ICalFeedsSection 
            properties={properties}
            subscriptions={icalSubscriptions}
            onRefresh={loadData}
            copyToClipboard={copyToClipboard}
            copiedFeed={copiedFeed}
          />
        </TabsContent>

        {/* Widgets Tab */}
        <TabsContent value="widgets">
          <WidgetsSection embedded />
        </TabsContent>
      </Tabs>

      {/* Connect Dialog */}
      <Dialog open={connectDialog} onOpenChange={setConnectDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Connect to {selectedChannel?.name}</DialogTitle>
            <DialogDescription>
              Enter your API credentials to connect this channel. You can get these from {selectedChannel?.name}'s partner portal.
            </DialogDescription>
          </DialogHeader>
          
          {selectedChannel?.setup_instructions && (
            <div className="bg-blue-50 border border-blue-200 rounded-xl p-3 text-sm">
              <p className="font-semibold text-blue-800 mb-1">Setup Instructions:</p>
              <p className="text-blue-700 whitespace-pre-line">{selectedChannel.setup_instructions}</p>
              {selectedChannel.docs_url && (
                <a href={selectedChannel.docs_url} target="_blank" rel="noopener noreferrer" 
                   className="text-blue-600 hover:underline flex items-center gap-1 mt-2">
                  <ExternalLink className="w-3.5 h-3.5" /> View Documentation
                </a>
              )}
            </div>
          )}

          <div className="space-y-4 mt-4">
            {selectedChannel?.required_fields?.includes('api_key') && (
              <div>
                <Label>API Key *</Label>
                <Input
                  type="password"
                  value={credentials.api_key}
                  onChange={(e) => setCredentials({ ...credentials, api_key: e.target.value })}
                  placeholder="Enter API key"
                  className="mt-1"
                />
              </div>
            )}
            {selectedChannel?.required_fields?.includes('api_secret') && (
              <div>
                <Label>API Secret *</Label>
                <Input
                  type="password"
                  value={credentials.api_secret}
                  onChange={(e) => setCredentials({ ...credentials, api_secret: e.target.value })}
                  placeholder="Enter API secret"
                  className="mt-1"
                />
              </div>
            )}
            {selectedChannel?.required_fields?.includes('partner_id') && (
              <div>
                <Label>Partner ID *</Label>
                <Input
                  value={credentials.partner_id}
                  onChange={(e) => setCredentials({ ...credentials, partner_id: e.target.value })}
                  placeholder="Enter partner/account ID"
                  className="mt-1"
                />
              </div>
            )}
            {selectedChannel?.required_fields?.includes('hotel_id') && (
              <div>
                <Label>Hotel/Property ID *</Label>
                <Input
                  value={credentials.hotel_id}
                  onChange={(e) => setCredentials({ ...credentials, hotel_id: e.target.value })}
                  placeholder="Enter hotel ID from the platform"
                  className="mt-1"
                />
              </div>
            )}
          </div>

          <div className="flex justify-end gap-2 mt-4">
            <Button variant="outline" onClick={() => setConnectDialog(false)}>Cancel</Button>
            <Button 
              onClick={handleConnect} 
              disabled={processing}
              className="bg-airbnb-pink hover:bg-airbnb-dark-pink text-white"
            >
              {processing ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Link2 className="w-4 h-4 mr-2" />}
              Connect
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Map Property Dialog */}
      <Dialog open={mappingDialog} onOpenChange={setMappingDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Map Property to {selectedChannel?.name}</DialogTitle>
            <DialogDescription>
              Link your local property to its corresponding listing on {selectedChannel?.name}.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 mt-4">
            <div>
              <Label>Local Property</Label>
              <select
                value={selectedProperty}
                onChange={(e) => setSelectedProperty(e.target.value)}
                className="w-full mt-1 border rounded-xl px-3 py-2 text-sm"
              >
                <option value="">Select a property</option>
                {properties.map((p) => (
                  <option key={p.property_id} value={p.property_id}>{p.name}</option>
                ))}
              </select>
            </div>
            <div>
              <Label>{selectedChannel?.name} Property/Listing ID</Label>
              <Input
                value={otaPropertyId}
                onChange={(e) => setOtaPropertyId(e.target.value)}
                placeholder="Enter the ID from the OTA platform"
                className="mt-1"
              />
              <p className="text-xs text-gray-500 mt-1">
                Find this in your {selectedChannel?.name} dashboard under property settings
              </p>
            </div>
          </div>

          <div className="flex justify-end gap-2 mt-4">
            <Button variant="outline" onClick={() => setMappingDialog(false)}>Cancel</Button>
            <Button 
              onClick={handleMapProperty} 
              disabled={processing || !selectedProperty || !otaPropertyId}
              className="bg-airbnb-pink hover:bg-airbnb-dark-pink text-white"
            >
              {processing ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Building2 className="w-4 h-4 mr-2" />}
              Map Property
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Channel Card Component
const ChannelCard = ({ channel, onConnect, onSync, onDisconnect, onMapProperty, getStatusIcon, getChannelIcon, comingSoon }) => (
  <div
    data-testid={`channel-${channel.code}`}
    className={`bg-white rounded-2xl border p-5 transition-all hover:shadow-md ${
      channel.connected ? 'border-emerald-200' : comingSoon ? 'border-amber-100 bg-amber-50/30' : 'border-gray-100'
    }`}
  >
    <div className="flex items-start justify-between mb-4">
      <div className="flex items-center gap-3">
        <span className="text-3xl">{getChannelIcon(channel.code)}</span>
        <div>
          <h3 className="font-bold text-airbnb-gray">{channel.name}</h3>
          <div className="flex items-center gap-1.5 mt-0.5">
            {getStatusIcon(channel)}
            <span className={`text-xs font-medium ${
              channel.connected ? 'text-emerald-600' : comingSoon ? 'text-amber-600' : 'text-gray-500'
            }`}>
              {channel.connected ? 'Connected' : comingSoon ? 'Coming Soon' : 'Not Connected'}
            </span>
          </div>
        </div>
      </div>
      {channel.connected && (
        <span className="px-2 py-1 bg-emerald-100 text-emerald-700 text-xs font-medium rounded-full">
          Active
        </span>
      )}
    </div>

    {/* Features */}
    <div className="flex flex-wrap gap-1.5 mb-4">
      {channel.features?.slice(0, 4).map((feature) => (
        <span key={feature} className="px-2 py-0.5 bg-gray-100 text-gray-600 text-xs rounded-full capitalize">
          {feature}
        </span>
      ))}
    </div>

    {/* Connection Details for Connected Channels */}
    {channel.connected && (
      <div className="bg-gray-50 rounded-xl p-3 mb-4 text-sm">
        <div className="flex items-center justify-between">
          <span className="text-gray-600">Properties Mapped</span>
          <span className="font-semibold text-airbnb-gray">{channel.property_count}</span>
        </div>
        {channel.last_sync && (
          <div className="flex items-center justify-between mt-1">
            <span className="text-gray-600">Last Sync</span>
            <span className="text-airbnb-gray">
              {new Date(channel.last_sync).toLocaleString()}
            </span>
          </div>
        )}
      </div>
    )}

    {/* Setup Instructions for Available Channels */}
    {!channel.connected && !comingSoon && channel.setup_instructions && (
      <div className="bg-blue-50 rounded-xl p-3 mb-4 text-xs text-blue-700">
        <p className="font-semibold mb-1">How to connect:</p>
        <p className="whitespace-pre-line">{channel.setup_instructions}</p>
      </div>
    )}

    {/* Actions */}
    <div className="flex items-center gap-2">
      {comingSoon ? (
        <Button
          variant="outline"
          size="sm"
          className="flex-1 rounded-xl text-sm"
          onClick={() => window.open(channel.docs_url, '_blank')}
        >
          <ExternalLink className="w-3.5 h-3.5 mr-1.5" />
          View Docs
        </Button>
      ) : channel.connected ? (
        <>
          <Button
            variant="outline"
            size="sm"
            className="flex-1 rounded-xl text-sm"
            onClick={onMapProperty}
          >
            <Building2 className="w-3.5 h-3.5 mr-1.5" />
            Map Property
          </Button>
          <Button
            variant="outline"
            size="sm"
            className="rounded-xl"
            onClick={() => onSync(channel)}
          >
            <RefreshCw className="w-3.5 h-3.5" />
          </Button>
          <Button
            variant="outline"
            size="sm"
            className="rounded-xl text-red-500 hover:text-red-600 hover:border-red-200"
            onClick={() => onDisconnect(channel)}
          >
            <Unlink2 className="w-3.5 h-3.5" />
          </Button>
        </>
      ) : (
        <Button
          size="sm"
          className="flex-1 rounded-xl text-sm bg-airbnb-pink hover:bg-airbnb-dark-pink text-white"
          onClick={onConnect}
        >
          <Link2 className="w-3.5 h-3.5 mr-1.5" />
          Connect
        </Button>
      )}
    </div>
  </div>
);

// Channel Health Dashboard Component
const ChannelHealthDashboard = ({ channels, healthMetrics, icalSubscriptions = [], onSync }) => {
  const connectedChannels = channels.filter(c => c.connected);
  const healthyCount = connectedChannels.filter(c => c.connection_status === 'connected').length;
  const errorCount = connectedChannels.filter(c => c.connection_status === 'error').length;
  
  // iCal sync stats
  const activeIcalSubs = icalSubscriptions.filter(s => s.is_active !== false);
  const icalErrors = icalSubscriptions.filter(s => s.last_error);
  const lastIcalSync = icalSubscriptions.reduce((latest, s) => {
    if (!s.last_sync) return latest;
    return !latest || new Date(s.last_sync) > new Date(latest) ? s.last_sync : latest;
  }, null);

  return (
    <div className="space-y-6">
      {/* Overview Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white rounded-2xl border border-gray-100 p-5">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-blue-100 flex items-center justify-center">
              <Globe className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-airbnb-gray">{connectedChannels.length}</p>
              <p className="text-sm text-gray-500">OTA Channels</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-2xl border border-gray-100 p-5">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-purple-100 flex items-center justify-center">
              <Calendar className="w-5 h-5 text-purple-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-purple-600">{activeIcalSubs.length}</p>
              <p className="text-sm text-gray-500">iCal Feeds</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-2xl border border-gray-100 p-5">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-emerald-100 flex items-center justify-center">
              <CheckCircle className="w-5 h-5 text-emerald-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-emerald-600">{healthyCount + activeIcalSubs.length - icalErrors.length}</p>
              <p className="text-sm text-gray-500">Healthy</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-2xl border border-gray-100 p-5">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-red-100 flex items-center justify-center">
              <AlertTriangle className="w-5 h-5 text-red-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-red-600">{errorCount + icalErrors.length}</p>
              <p className="text-sm text-gray-500">Errors</p>
            </div>
          </div>
        </div>
      </div>

      {/* Sync Frequency Info */}
      <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 flex items-start gap-3">
        <Clock className="w-5 h-5 text-blue-600 mt-0.5" />
        <div>
          <p className="font-semibold text-blue-800">Sync Frequency</p>
          <p className="text-sm text-blue-700 mt-1">
            iCal feeds sync at configurable intervals: <strong>15, 30, or 60 minutes</strong>. 
            OTA channels sync when triggered manually or via webhooks when credentials are connected.
          </p>
        </div>
      </div>

      {/* iCal Sync Status */}
      {activeIcalSubs.length > 0 && (
        <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
          <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
            <h3 className="font-semibold text-airbnb-gray">iCal Feed Status</h3>
            <span className="text-xs text-gray-500">
              Last sync: {lastIcalSync ? new Date(lastIcalSync).toLocaleString() : 'Never'}
            </span>
          </div>
          <div className="divide-y divide-gray-100">
            {activeIcalSubs.map((sub) => (
              <div key={sub.subscription_id} className="px-5 py-4 flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className={`w-3 h-3 rounded-full ${
                    sub.last_error ? 'bg-red-500' : sub.last_sync ? 'bg-emerald-500' : 'bg-amber-500'
                  }`} />
                  <div>
                    <p className="font-medium text-airbnb-gray">{sub.source_name}</p>
                    <p className="text-sm text-gray-500">
                      Property: {sub.property_id} • Interval: {sub.poll_interval_minutes || 60} min
                    </p>
                    {sub.last_sync && (
                      <p className="text-xs text-gray-400">
                        Last sync: {new Date(sub.last_sync).toLocaleString()}
                      </p>
                    )}
                  </div>
                </div>
                <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                  sub.last_error 
                    ? 'bg-red-100 text-red-700'
                    : sub.last_sync
                    ? 'bg-emerald-100 text-emerald-700'
                    : 'bg-amber-100 text-amber-700'
                }`}>
                  {sub.last_error ? 'Error' : sub.last_sync ? 'Healthy' : 'Pending'}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* OTA Channel Status Table */}
      {connectedChannels.length > 0 ? (
        <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
          <div className="px-5 py-4 border-b border-gray-100">
            <h3 className="font-semibold text-airbnb-gray">OTA Channel Status</h3>
          </div>
          <div className="divide-y divide-gray-100">
            {connectedChannels.map((channel) => (
              <div key={channel.code} className="px-5 py-4 flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className={`w-3 h-3 rounded-full ${
                    channel.connection_status === 'connected' ? 'bg-emerald-500' :
                    channel.connection_status === 'error' ? 'bg-red-500' : 'bg-amber-500'
                  }`} />
                  <div>
                    <p className="font-medium text-airbnb-gray">{channel.name}</p>
                    <p className="text-sm text-gray-500">
                      {channel.property_count} properties • Last sync: {
                        channel.last_sync 
                          ? new Date(channel.last_sync).toLocaleString()
                          : 'Never'
                      }
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                    channel.connection_status === 'connected' 
                      ? 'bg-emerald-100 text-emerald-700'
                      : channel.connection_status === 'error'
                      ? 'bg-red-100 text-red-700'
                      : 'bg-amber-100 text-amber-700'
                  }`}>
                    {channel.connection_status === 'connected' ? 'Healthy' : 
                     channel.connection_status === 'error' ? 'Error' : 'Syncing'}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    className="rounded-xl"
                    onClick={() => onSync(channel)}
                  >
                    <RefreshCw className="w-3.5 h-3.5 mr-1" />
                    Sync Now
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      ) : activeIcalSubs.length === 0 && (
        <div className="bg-white rounded-2xl border border-gray-100 p-10 text-center">
          <Globe className="w-12 h-12 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-600 mb-2">No Integrations Active</h3>
          <p className="text-gray-500 mb-4">Connect to OTA channels or add iCal feeds to see their health status here.</p>
        </div>
      )}
    </div>
  );
};

// iCal Feeds Section Component
const ICalFeedsSection = ({ properties, subscriptions, onRefresh, copyToClipboard, copiedFeed }) => {
  const [selectedProperty, setSelectedProperty] = useState('');
  const [feedUrl, setFeedUrl] = useState('');
  const [feedToken, setFeedToken] = useState('');
  const [generating, setGenerating] = useState(false);
  const [newSubUrl, setNewSubUrl] = useState('');
  const [newSubName, setNewSubName] = useState('');
  const [newSyncInterval, setNewSyncInterval] = useState(60);
  const [addingSubscription, setAddingSubscription] = useState(false);

  const API = process.env.REACT_APP_BACKEND_URL;

  const generateFeed = async (propertyId) => {
    if (!propertyId) return;
    setGenerating(true);
    try {
      const response = await fetch(`${API}/api/ical/feed/${propertyId}/generate`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });
      const data = await response.json();
      setFeedToken(data.token);
      setFeedUrl(`${API}/api/ical/feed/${propertyId}/${data.token}`);
    } catch (err) {
      console.error('Failed to generate feed:', err);
    } finally {
      setGenerating(false);
    }
  };

  const addSubscription = async () => {
    if (!selectedProperty || !newSubUrl) return;
    setAddingSubscription(true);
    try {
      await icalSyncAPI.createSubscription({
        property_id: selectedProperty,
        feed_url: newSubUrl,
        source_name: newSubName || 'External Calendar',
        poll_interval_minutes: newSyncInterval
      });
      setNewSubUrl('');
      setNewSubName('');
      setNewSyncInterval(60);
      onRefresh();
      toast.success('iCal subscription added', { 
        description: `Will sync every ${newSyncInterval} minutes` 
      });
    } catch (err) {
      toast.error('Failed to add subscription', { 
        description: err.response?.data?.detail || 'Please check the feed URL'
      });
    } finally {
      setAddingSubscription(false);
    }
  };

  const deleteSubscription = async (subId) => {
    if (!window.confirm('Remove this calendar subscription?')) return;
    try {
      await icalSyncAPI.deleteSubscription(subId);
      onRefresh();
      toast.success('Subscription removed');
    } catch (err) {
      toast.error('Failed to remove subscription');
    }
  };

  const syncSubscription = async (subId) => {
    const sub = subscriptions.find(s => s.subscription_id === subId);
    toast.info('Sync started', { description: `Syncing ${sub?.source_name || 'calendar'}...` });
    try {
      await icalSyncAPI.syncNow(subId);
      setTimeout(() => {
        onRefresh();
        toast.success('Sync completed', { 
          description: `${sub?.source_name || 'Calendar'} synced successfully` 
        });
      }, 2000);
    } catch (err) {
      toast.error('Sync failed', { 
        description: err.response?.data?.detail || 'Could not sync calendar' 
      });
    }
  };

  // Group subscriptions by property
  const subscriptionsByProperty = subscriptions.reduce((acc, sub) => {
    if (!acc[sub.property_id]) acc[sub.property_id] = [];
    acc[sub.property_id].push(sub);
    return acc;
  }, {});

  return (
    <div className="space-y-6">
      {/* Info Banner */}
      <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 flex items-start gap-3">
        <Calendar className="w-5 h-5 text-blue-600 mt-0.5" />
        <div>
          <p className="font-semibold text-blue-800">Property-Specific Calendar Sync</p>
          <p className="text-sm text-blue-700 mt-1">
            Each property has its own iCal feed. Export your calendar to share with OTAs, or import external calendars to block dates automatically.
          </p>
        </div>
      </div>

      {/* Property Selector */}
      <div className="bg-white rounded-2xl border border-gray-100 p-5">
        <h3 className="font-semibold text-airbnb-gray mb-4">Select Property</h3>
        <select
          value={selectedProperty}
          onChange={(e) => {
            setSelectedProperty(e.target.value);
            setFeedUrl('');
            setFeedToken('');
          }}
          className="w-full border rounded-xl px-3 py-2 text-sm"
        >
          <option value="">Choose a property...</option>
          {properties.map((p) => (
            <option key={p.property_id} value={p.property_id}>{p.name}</option>
          ))}
        </select>
      </div>

      {selectedProperty && (
        <>
          {/* Export Feed Section */}
          <div className="bg-white rounded-2xl border border-gray-100 p-5">
            <h3 className="font-semibold text-airbnb-gray mb-2">Export Calendar (iCal Feed)</h3>
            <p className="text-sm text-gray-500 mb-4">
              Share this URL with Airbnb, Booking.com, or other platforms to keep calendars in sync.
            </p>
            
            {feedUrl ? (
              <div className="bg-gray-50 rounded-xl p-3 flex items-center gap-2">
                <Input 
                  value={feedUrl} 
                  readOnly 
                  className="flex-1 bg-white text-sm"
                />
                <Button
                  variant="outline"
                  size="sm"
                  className="rounded-xl"
                  onClick={() => copyToClipboard(feedUrl, 'export')}
                >
                  {copiedFeed === 'export' ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                </Button>
              </div>
            ) : (
              <Button
                onClick={() => generateFeed(selectedProperty)}
                disabled={generating}
                className="bg-airbnb-pink hover:bg-airbnb-dark-pink text-white rounded-xl"
              >
                {generating ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Link2 className="w-4 h-4 mr-2" />}
                Generate Feed URL
              </Button>
            )}
          </div>

          {/* Import/Subscribe Section */}
          <div className="bg-white rounded-2xl border border-gray-100 p-5">
            <h3 className="font-semibold text-airbnb-gray mb-2">Import Calendar (Subscribe)</h3>
            <p className="text-sm text-gray-500 mb-4">
              Add external calendar feeds to automatically block dates from other platforms.
            </p>
            
            <div className="space-y-3">
              <Input
                value={newSubUrl}
                onChange={(e) => setNewSubUrl(e.target.value)}
                placeholder="https://www.airbnb.com/calendar/ical/..."
                className="text-sm"
              />
              <div className="flex gap-2">
                <Input
                  value={newSubName}
                  onChange={(e) => setNewSubName(e.target.value)}
                  placeholder="Source name (e.g., Airbnb)"
                  className="flex-1 text-sm"
                />
                <select
                  value={newSyncInterval}
                  onChange={(e) => setNewSyncInterval(Number(e.target.value))}
                  className="h-10 px-3 text-sm border border-gray-200 rounded-xl bg-white"
                  data-testid="sync-interval-select"
                >
                  <option value={15}>Every 15 min</option>
                  <option value={30}>Every 30 min</option>
                  <option value={60}>Every 60 min</option>
                </select>
                <Button
                  onClick={addSubscription}
                  disabled={addingSubscription || !newSubUrl}
                  className="bg-airbnb-pink hover:bg-airbnb-dark-pink text-white rounded-xl"
                >
                  {addingSubscription ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Subscribe'}
                </Button>
              </div>
            </div>

            {/* Existing Subscriptions for this property */}
            {subscriptionsByProperty[selectedProperty]?.length > 0 && (
              <div className="mt-4 space-y-2">
                <p className="text-sm font-medium text-gray-700">Active Subscriptions:</p>
                {subscriptionsByProperty[selectedProperty].map((sub) => (
                  <div key={sub.subscription_id} className="flex items-center justify-between bg-gray-50 rounded-xl p-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="font-medium text-sm text-airbnb-gray">{sub.source_name}</p>
                        <span className="text-xs text-gray-400 px-1.5 py-0.5 bg-gray-200 rounded-full">
                          {sub.poll_interval_minutes || 60} min
                        </span>
                      </div>
                      <p className="text-xs text-gray-500 truncate">{sub.feed_url}</p>
                      <p className="text-xs text-gray-400 mt-1">
                        {sub.last_sync ? `Last sync: ${new Date(sub.last_sync).toLocaleString()}` : 'Never synced'}
                        {sub.last_error && <span className="text-red-500 ml-2">• Error</span>}
                      </p>
                    </div>
                    <div className="flex items-center gap-1 ml-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-8 w-8 p-0"
                        onClick={() => syncSubscription(sub.subscription_id)}
                      >
                        <RefreshCw className="w-3.5 h-3.5" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-8 w-8 p-0 text-red-500 hover:text-red-600"
                        onClick={() => deleteSubscription(sub.subscription_id)}
                      >
                        <XCircle className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </>
      )}

      {/* All Properties iCal Summary */}
      <div className="bg-white rounded-2xl border border-gray-100 p-5">
        <h3 className="font-semibold text-airbnb-gray mb-4">All Properties - iCal Status</h3>
        <div className="space-y-3">
          {properties.map((property) => {
            const propertySubs = subscriptionsByProperty[property.property_id] || [];
            return (
              <div key={property.property_id} className="flex items-center justify-between py-2 border-b border-gray-100 last:border-0">
                <div>
                  <p className="font-medium text-airbnb-gray">{property.name}</p>
                  <p className="text-xs text-gray-500">
                    {propertySubs.length} subscription{propertySubs.length !== 1 ? 's' : ''}
                  </p>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  className="rounded-xl text-xs"
                  onClick={() => setSelectedProperty(property.property_id)}
                >
                  Manage
                </Button>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default IntegrationsPage;
